// A minimal .xlsx writer.
//
// The payments screen already had to hand-roll a CSV parser and a CSV writer to
// avoid taking on a dependency for a handful of rows, and the same reasoning
// applies here: an .xlsx is a ZIP of small XML parts, and the files this app
// produces have one sheet, a few columns and inline strings. Rather than pull in
// SheetJS (whose published npm version is stale and carries advisories) for
// that, the packaging is written out here.
//
// Entries are stored uncompressed. The data is tiny, and "store" keeps the
// implementation to a CRC and two headers instead of a deflate integration.
//
// Styling is deliberately narrow: a coloured, bold header row and explicit
// column widths. That is enough to make the sheet presentable without a general
// style model, and both are just extra XML parts referenced from the sheet.

const textEncoder = new TextEncoder();

const xmlEscape = value =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

// The standard table-driven CRC-32, which every ZIP reader checks each entry
// against on open. A wrong value here makes the file un-openable rather than
// merely odd, so it is the one part that has to be exactly right.
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

const crc32 = bytes => {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
};

// Fixed DOS timestamp (2020-01-01 00:00). Reproducible output matters more than
// the date here, and nothing reads it.
const DOS_TIME = 0;
const DOS_DATE = ((2020 - 1980) << 9) | (1 << 5) | 1;

// Builds a ZIP archive holding the given entries, all stored (method 0).
function zipStore(entries) {
    const chunks = [];
    const central = [];
    let offset = 0;

    const u16 = (view, pos, value) => { view.setUint16(pos, value, true); };
    const u32 = (view, pos, value) => { view.setUint32(pos, value >>> 0, true); };

    for (const { name, data } of entries) {
        const nameBytes = textEncoder.encode(name);
        const crc = crc32(data);
        const size = data.length;

        const local = new Uint8Array(30 + nameBytes.length);
        const localView = new DataView(local.buffer);
        u32(localView, 0, 0x04034b50);
        u16(localView, 4, 20);
        u16(localView, 6, 0x0800); // UTF-8 filename
        u16(localView, 8, 0); // stored
        u16(localView, 10, DOS_TIME);
        u16(localView, 12, DOS_DATE);
        u32(localView, 14, crc);
        u32(localView, 18, size);
        u32(localView, 22, size);
        u16(localView, 26, nameBytes.length);
        u16(localView, 28, 0);
        local.set(nameBytes, 30);

        chunks.push(local, data);

        const cd = new Uint8Array(46 + nameBytes.length);
        const cdView = new DataView(cd.buffer);
        u32(cdView, 0, 0x02014b50);
        u16(cdView, 4, 20);
        u16(cdView, 6, 20);
        u16(cdView, 8, 0x0800);
        u16(cdView, 10, 0);
        u16(cdView, 12, DOS_TIME);
        u16(cdView, 14, DOS_DATE);
        u32(cdView, 16, crc);
        u32(cdView, 20, size);
        u32(cdView, 24, size);
        u16(cdView, 28, nameBytes.length);
        u16(cdView, 30, 0);
        u16(cdView, 32, 0);
        u16(cdView, 34, 0);
        u16(cdView, 36, 0);
        u32(cdView, 38, 0);
        u32(cdView, 42, offset);
        cd.set(nameBytes, 46);
        central.push(cd);

        offset += local.length + size;
    }

    const centralSize = central.reduce((sum, cd) => sum + cd.length, 0);
    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    u32(endView, 0, 0x06054b50);
    u16(endView, 4, 0);
    u16(endView, 6, 0);
    u16(endView, 8, central.length);
    u16(endView, 10, central.length);
    u32(endView, 12, centralSize);
    u32(endView, 16, offset);
    u16(endView, 20, 0);

    return concat([...chunks, ...central, end]);
}

function concat(arrays) {
    const total = arrays.reduce((sum, a) => sum + a.length, 0);
    const out = new Uint8Array(total);
    let pos = 0;
    for (const a of arrays) {
        out.set(a, pos);
        pos += a.length;
    }
    return out;
}

const columnName = index => String.fromCharCode(65 + index);

// Column widths in Excel's character units. The header style is cellXf index 1
// (see STYLES): white bold text on a solid fill.
const HEADER_STYLE = 1;

function colsXml(widths) {
    if (!widths || widths.length === 0) return '';
    const cols = widths.map((width, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`
    ).join('');
    return `<cols>${cols}</cols>`;
}

// Renders rows (an array of arrays of strings/numbers) as a single-sheet
// worksheet. Inline strings avoid a shared-strings part; numbers are written as
// bare values so Excel treats the total as a number, not text. The first row is
// treated as a header and styled.
function sheetXml(rows, widths) {
    const cellXml = (value, colIndex, rowIndex) => {
        const ref = `${columnName(colIndex)}${rowIndex + 1}`;
        const style = rowIndex === 0 ? ` s="${HEADER_STYLE}"` : '';
        if (typeof value === 'number' && Number.isFinite(value)) {
            return `<c r="${ref}"${style}><v>${value}</v></c>`;
        }
        return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(value ?? '')}</t></is></c>`;
    };

    const rowXml = rows
        .map((cells, rowIndex) =>
            `<row r="${rowIndex + 1}">${cells.map((cell, i) => cellXml(cell, i, rowIndex)).join('')}</row>`
        )
        .join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
        `${colsXml(widths)}<sheetData>${rowXml}</sheetData></worksheet>`;
}

const CONTENT_TYPES =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `</Types>`;

const ROOT_RELS =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

const workbookXml = sheetName =>
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets><sheet name="${xmlEscape(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;

const WORKBOOK_RELS =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

// Two cell formats: the default, and a header style. Excel insists the first two
// fills are "none" and "gray125", so the header's solid fill is the third.
const STYLES =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<fonts count="2">` +
    `<font><sz val="11"/><name val="Calibri"/></font>` +
    `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>` +
    `</fonts>` +
    `<fills count="3">` +
    `<fill><patternFill patternType="none"/></fill>` +
    `<fill><patternFill patternType="gray125"/></fill>` +
    `<fill><patternFill patternType="solid"><fgColor rgb="FF1F4E78"/><bgColor indexed="64"/></patternFill></fill>` +
    `</fills>` +
    `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
    `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="2">` +
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
    `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1">` +
    `<alignment horizontal="center" vertical="center"/></xf>` +
    `</cellXfs>` +
    `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
    `</styleSheet>`;

// The bytes of a one-sheet .xlsx. Split from the Blob so it can be exercised
// (unzipped and parsed) without a browser. `columnWidths` is an array of widths
// in character units, one per column.
export function buildXlsxBytes(rows, sheetName = 'Sheet1', columnWidths = []) {
    const encode = value => textEncoder.encode(value);

    return zipStore([
        { name: '[Content_Types].xml', data: encode(CONTENT_TYPES) },
        { name: '_rels/.rels', data: encode(ROOT_RELS) },
        { name: 'xl/workbook.xml', data: encode(workbookXml(sheetName)) },
        { name: 'xl/_rels/workbook.xml.rels', data: encode(WORKBOOK_RELS) },
        { name: 'xl/styles.xml', data: encode(STYLES) },
        { name: 'xl/worksheets/sheet1.xml', data: encode(sheetXml(rows, columnWidths)) },
    ]);
}

export const XLSX_MIME =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function buildXlsxBlob(rows, sheetName = 'Sheet1', columnWidths = []) {
    return new Blob([buildXlsxBytes(rows, sheetName, columnWidths)], { type: XLSX_MIME });
}
